import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { Component, Input, OnInit, inject } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TotalFillUpsModal } from '../../modals/total-fill-ups-modal/total-fill-ups-modal.component';
import { FuelKpiRequest } from '../../../../_shared';
import { formatCost, formatCostDifference, formatDropdownValue, getEnumFromString } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { AuthService, FilterService } from '../../../../../_shared/services';
import { AccountFilterData, DateIntervalFilterData } from '../../../../../_shared/models';
import { FuelChangesService } from '../../../services/fuel-changes.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { isUptrendPositive } from '../../../../../_shared/helpers/kpiWidget/dropdown-measurement.helper';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { DropdownOption, KpiWidgetComponent } from '../../../../../_shared/components/kpi-widget/kpi-widget.component';
import { MeasurementTypes } from '../../../../../_shared/enums/kpiStats/measurement-types.enum';

@Component({
	selector: 'app-total-fill-ups-widget',
	standalone: true,
	imports: [TranslocoPipe, KpiWidgetComponent],
	templateUrl: './total-fill-ups-widget.component.html',
	styleUrl: './total-fill-ups-widget.component.scss'
})
export class TotalFillUpsWidgetComponent extends BaseWidgetComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
	@Input() selectedFilter: string;
	@Input() accountFilter: AccountFilterData = {} as AccountFilterData;
	@Input() dateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;
	@Input() modalFilterHeaders: { previous: string; current: string } = { previous: '', current: '' };

	icon: string = '';
	totalFillUpsPercentageChange: any;
	selectedUnit: string = MeasurementTypes.Volume.toString();
	errorMessage: string = '';
	currencyCode: string = '';
	currencySymbol: string = '';
	hasError: boolean = false;
	dropdownOptions: DropdownOption[] = []
	request: FuelKpiRequest;

	constructor(
		private modalService: NgbModal,
		protected override filterService: FilterService,
		private fuelChangesService: FuelChangesService,
		private errorHandlerService: ErrorHandlerService,
		private authService: AuthService
	) {
		super(filterService);
		this.title = 'Total Fill-Ups';
		this.icon = 'assets/images-fuel/fuelPumpIcon.svg';
		this.isLoading = true;
		this.value = 0;
		this.difference = 0;
	}

	async onFilterChangeAsync(): Promise<void> {
		await this.getTotalFillUpsKpiStats();
	}

	ngOnInit(): void {
		this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR'
		this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
		this.dropdownOptions = [
			{ label: this.transloco.translate('fuelDashboard.units.litres'), value: MeasurementTypes.Volume.toString(), unit: 'ℓ' },
			{ label: this.transloco.translate('fuelDashboard.units.costPerLitre', { currency: this.currencyCode }), value: MeasurementTypes.CostPerUnit.toString(), unit: ' /ℓ' }
		]
		this.selectedUnit = this.dropdownOptions[0].value;
	}

	formatValue(value: number): string {
		return formatDropdownValue(value, this.selectedUnit, this.dropdownOptions, getEnumFromString, this.currencySymbol);
	}

	formatCost(value: number): string {
		return formatCost(value, this.currencySymbol);
	}

	formatCostDifference(value: number): string {
		return formatCostDifference(value, this.selectedFilter, this.currencySymbol, (key, params) => this.transloco.translate(key, params));
	}

	async onDropdownChanged(selectedOption: DropdownOption): Promise<void> {
		this.selectedUnit = selectedOption.value;
		this.request.fuelMeasurementType = getEnumFromString(selectedOption.value);
		this.getTotalFillUpsKpiStats();
	}

	get isUptrendPositive(): boolean {
		return isUptrendPositive(getEnumFromString(this.selectedUnit));
	}

	async getTotalFillUpsKpiStats(): Promise<void> {
		this.isLoading = true;
		this.hasError = false;

		try {
			this.request = {
				accountIds: this.widgetFilter.accountIds,
				timeFrom: this.widgetFilter.startDate!,
				timeTo: this.widgetFilter.endDate!,
				previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
				previousTimeTo: this.widgetFilter.previousCycleEndDate!,
				fuelMeasurementType: getEnumFromString(this.selectedUnit)
			}

			this.cancelSubject.next();
			const response = await this.fuelChangesService.getTotalFillUpsKpiStats(this.request, this.cancelSubject);
			this.value = response.data?.totalValue ?? 0;
			this.totalFillUpsPercentageChange = response.data?.percentageChange;
			this.difference = response.data?.difference ?? 0;
			this.totalCost = response.data?.totalCost ?? 0;
			this.isLoading = false;
		} catch (error: any) {
			this.errorHandlerService.handleError(error, {
				callback: (message) => {
					this.errorMessage = message;
					this.hasError = true;
					this.isLoading = false;
				}
			});
		}
	}

	openTotalFillUpsModal(): void {
		const modalOptions: NgbModalOptions = {
			centered: true,
			backdrop: 'static',
			size: 'xl',
			windowClass: 'custom-modal-class',
		};
		const modalRef = this.modalService.open(TotalFillUpsModal, modalOptions);
		modalRef.componentInstance.request = this.request;
		modalRef.componentInstance.modalFilterHeaders = this.modalFilterHeaders;
	}
}